// Catalog.ID Client — Kit Cover
// ============================================================================
// Page 1 of the kit (registration-restructure §5, round 4; designer's cover
// artwork adopted 2026-09-08): the name of the thing in the reader's hands,
// the account it belongs to, the version stamp, and the one instruction that
// must be read before anything else in the kit is used. No secret material —
// it is the page that ends up face-up on a desk or in a safe.
//
// The artwork is design/coverpage.svg: the Catalog lockup alone on a white
// page, painted by pdfmake as the page-1 background (`coverArtwork` in the
// print options → letterhead.ts's `kitCoverBackground`). Everything here is
// laid out AGAINST that artwork, so the numbers below are page coordinates
// measured off the designer's mock-up, not flow margins:
//
//   logo lockup            179–266 pt from the top edge (in the SVG)
//   kit title              baseline ≈ 359 pt, 26 pt bold, centered
//   Account / … / Version  centered lines from ≈ 410 pt, 15 pt, one per row
//   warning block          pinned at 612 pt — "PRINT TWICE ONLY, …" — and the
//                          storage note under it, ≈ 355 pt wide
//
// The warning block is pinned with pdfmake's `absolutePosition` rather than
// flowed, so it sits at the same height on a personal cover (two account
// lines) and a delegate cover (up to five). Its x is the left page margin,
// which makes the detached block exactly the content width, so `alignment:
// 'center'` centers on the page.
//
// What the cover names, per kit kind (decided 2026-09-08): the account id,
// the organization rows an organization or delegate kit has (Organization,
// Domain, Role) and the version. The account TYPE is not a row any more —
// the title says it — and a cover NEVER carries an email or a phone number:
// personal and delegate covers name no contact channel, as the organization
// cover names only its domain.

import type { Content } from 'pdfmake/interfaces';
import { LETTERHEAD_PAGE_MARGINS_A4 } from '../letterhead-background';
import { shortKitTitle, type AccountHeaderData } from './shared';

export interface CoverPageData {
    /** The kit's full printed name — see `bookletTitle`. The cover prints it
     *  without the "Catalog.ID " prefix: the lockup right above carries it. */
    title: string;
    header: AccountHeaderData;
    /** Present when the kit carries Account Recovery Sheets: the storage note
     *  then names them and the k-of-n rule. Absent or null ⇒ the copies
     *  sentence alone (a delegate kit, a kit printed without a recovery kit). */
    recoveryKit?: { thresholdK: number; totalSharesN: number } | null;
}

/** The instruction every kit opens with, as the designer set it: two lines,
 *  all capitals, no trailing punctuation. */
export const COVER_WARNING = 'PRINT TWICE ONLY, DO NOT SCAN\nAND DO NOT SAVE ON YOUR DEVICE';

export const COVER_NOTE_COPIES =
    'Keep both copies physically separated, so that in case of a fire not both copies are lost.';

/** Only when the kit actually contains recovery sheets. */
export function coverNoteRecovery(thresholdK: number, totalSharesN: number): string {
    return 'This kit also contains Account Recovery Sheets, which you can hand to your notary '
        + 'public, a relative or other third parties you trust. '
        + `Any ${thresholdK} of the ${totalSharesN} sheets are needed to recover your account.`;
}

// ── geometry (pt from the top edge of the A4 page) ──────────────────────────

/** Top of the title's text block: its baseline lands ≈ 359 pt, well under
 *  the lockup, whose tagline ends ≈ 266 pt (design/coverpage.svg). */
const TITLE_TOP = 335;

/** Space between the title and the first account line. */
const ACCOUNT_LINES_GAP = 30;

/** Top of the pinned warning block.
 *
 *  PAGE BUDGET: a detached block may not extend into the bottom page margin —
 *  pdfmake caps it at (841.89 − y − 80) pt and silently drops the lines that
 *  do not fit (the first cut lost "account." off the note, 2026-09-08). From
 *  612 the block has 149.9 pt; the warning takes 2 × 25.3, the gap 10, and the
 *  longest note (recovery sentence included) 5 lines × 15.4 = 77 at 10.5 pt /
 *  1.25 — 137.6 in all, 12 pt spare. A sixth note line would NOT fit: keep
 *  the note's text under the length the test pins, and re-run
 *  `render-booklet.ts --png` after touching anything on this page. */
const WARNING_TOP = 612;

/** Left/right inset of the storage note inside the content width, so the
 *  paragraph is ≈ 355 pt wide as in the mock-up. */
const NOTE_INSET = 60;

/** Space between the warning's last line and the note. */
const NOTE_GAP = 10;

export function buildCoverPage(data: CoverPageData): Content[] {
    const [leftMargin, topMargin] = LETTERHEAD_PAGE_MARGINS_A4;
    const header = data.header;

    const lines: Content[] = [coverLine('Account', { text: header.cid, style: 'coverCid' })];
    if (header.orgCid) lines.push(coverLine('Organization', header.orgCid));
    if (header.orgDomain) lines.push(coverLine('Domain', header.orgDomain));
    if (header.orgRole) lines.push(coverLine('Role', header.orgRole));
    lines.push(coverLine('Version', header.version));

    const note = [COVER_NOTE_COPIES];
    if (data.recoveryKit) {
        note.push(coverNoteRecovery(data.recoveryKit.thresholdK, data.recoveryKit.totalSharesN));
    }

    return [
        { text: shortKitTitle(data.title), style: 'coverTitle', margin: [0, TITLE_TOP - topMargin, 0, 0] },
        { stack: lines, style: 'coverLine', margin: [0, ACCOUNT_LINES_GAP, 0, 0] },
        {
            absolutePosition: { x: leftMargin, y: WARNING_TOP },
            stack: [
                { text: COVER_WARNING, style: 'coverWarning' },
                { text: note.join(' '), style: 'coverNote', margin: [NOTE_INSET, NOTE_GAP, NOTE_INSET, 0] },
            ],
        },
    ];
}

/** "Label: value" on one centered line; the value may carry its own style
 *  (the account id does). */
function coverLine(label: string, value: string | Content): Content {
    return { text: [`${label}: `, value] };
}
